import { SessionState, SessionTickSchedule } from './app-lifecycles.models';

/** True once the user is authenticated and activity tracking has begun. */
export function isSessionTracking(session: SessionState): boolean {
  return session.lastActivityAt > 0;
}

/** Epoch ms at which the warning modal is due. */
export function sessionWarnsAt(session: SessionState): number {
  return session.lastActivityAt + session.idleWarningAfterMs;
}

/** Epoch ms at which the session is over. FR-2: 29 min + 60 s = 30 min. */
export function sessionExpiresAt(session: SessionState): number {
  return sessionWarnsAt(session) + session.warningDurationMs;
}

/** True while the countdown modal should be on screen. */
export function isSessionWarning(session: SessionState): boolean {
  return (
    isSessionTracking(session) &&
    session.nowMs >= sessionWarnsAt(session) &&
    session.nowMs < sessionExpiresAt(session)
  );
}

/** True once the countdown has run out and the session must be destroyed. */
export function isSessionExpired(session: SessionState): boolean {
  return (
    isSessionTracking(session) && session.nowMs >= sessionExpiresAt(session)
  );
}

/**
 * Whole seconds left on the countdown, clamped to 0. Rounded up so the modal
 * opens showing "60 seconds" rather than "59".
 */
export function sessionSecondsRemaining(session: SessionState): number {
  if (!isSessionTracking(session)) {
    return 0;
  }
  const remainingMs = sessionExpiresAt(session) - session.nowMs;
  return remainingMs <= 0 ? 0 : Math.ceil(remainingMs / 1000);
}

/** What the ticker should be doing, given the current session state. */
export function sessionTickSchedule(
  session: SessionState
): SessionTickSchedule {
  if (!isSessionTracking(session) || isSessionExpired(session)) {
    return { kind: 'stopped' };
  }
  if (isSessionWarning(session)) {
    return { kind: 'countdown' };
  }
  return { kind: 'idle', warnsAt: sessionWarnsAt(session) };
}

/**
 * Schedules are rebuilt on every state change, so compare by value — otherwise
 * the ticker would be torn down and recreated once per second during the
 * countdown.
 */
export function sameTickSchedule(
  a: SessionTickSchedule,
  b: SessionTickSchedule
): boolean {
  if (a.kind !== b.kind) {
    return false;
  }
  return a.kind === 'idle' && b.kind === 'idle'
    ? a.warnsAt === b.warnsAt
    : true;
}
